import { useState } from "react";

import { useMatchesMedia } from "@/hooks/use-matches-media";
import { OLD_NOTES_MAX, type NotesTableChanges, type OldValue } from "@/lib/spec-base-changes";
import { cn } from "@/lib/utils";

import { EditMenu } from "./edit-menu";
import { IconButton } from "./icon-button";
import { IconMenuButtonTrigger, MoreVertGlyph } from "./icon-menu-button";
import { MuiKeyboardArrowDownIcon, MuiKeyboardArrowUpIcon } from "./mui-arrow-icons";
import { MuiBasicTableCell, MuiBasicTableRow } from "./mui-basic-table";
import { MuiLink } from "./mui-link";
import { MuiTypography } from "./mui-typography";
import { MUI_TYPOGRAPHY_VARIANTS } from "./mui-typography-shape";
import { OldBesideNew } from "./old-beside-new";
import { RemovedRoomNotes } from "./room-change-list";
import { TruncatedNotes } from "./truncated-notes";

// The spec sheets' expandable notes row — the donor's `ExpandableNotesRow`
// (`components/specSheets/expandable-notes-row.tsx`), shared in the source
// beside `SpecSheetNotes` (ported as `truncated-notes.tsx`), so shared here
// (`docs/agents/migration-guidelines.md`, "Shared donor components port as
// shared Atlas components"). The Editor's per-location notes table is its
// first Atlas caller (#808); the parity enumeration on #808 (N2–N13) names the
// source line of every value below.
//
// An item's note and its rooms' notes, folded or open:
//
//   - **the counts**: rooms are the item's locations; notes are 1 for the
//     item's own note plus each room with a non-empty one;
//   - **the toggle** shows when there is a note and any room, or no note and
//     more than one room — a small `expand row` button, absolute at the top of
//     its cell, its click left to bubble;
//   - **it opens by itself** over more than one room, again whenever a new
//     rooms array arrives (the donor's `useEffect([itemLocations])`) — or
//     the caller's `reopenKey` moves, for a caller whose donor rebuilt the array
//     on writes that leave it unchanged — so a write re-opens a folded table;
//   - **what renders**: the `Note` row when there is a note or the table is
//     open over more than one room (its label empty without a note); the one
//     room while closed (so opening a noted line over one room *hides* it —
//     carried); every room while open over more than one; and, closed over
//     more than one, the summary `${n} Note(s) • A, B, C`;
//   - **a room's row**: its name, then its note and — unless finalized — a ⋮
//     opening the one-item edit menu, whose click stops at the ⋮; or, without a
//     note and unless finalized, an `Add a note` link whose click bubbles.
//     Finalized is the only gate: a struck line's controls stay live.
//
// **Changes from Base** (#1104; #1060 Q9; ledger §7, "Changed from base
// widens"), an Atlas addition for the Editor's table with the switch on: given
// `notesChanges`, a changed room note draws as its base text struck above it
// with the elbow (`OldBesideNew`'s block, old cut at 140) in the note's place,
// its ⋮ kept; a removed room note as `~~old~~ ↳ None` there with `Add a note`
// kept below it; the item's own changed note pairs in the Note row, which a
// removed item note keeps, labelled; and the line's removed rooms that had
// notes follow its rooms, struck over *None*, folding away with them
// (#1106 D8). Unset — the spec sheets' callers, the switch off — the row
// draws exactly as above.
//
// Desktop draws the donor's `ExpandedTableRow` — a toggle cell and a content
// cell. Below `sm` the donor draws a horizontal `Stack` straight into the
// table body; here that stack sits in a borderless, unpadded cell spanning the
// row, which is the box the browser's anonymous cell gives the donor's, in
// valid table markup.

/** `theme.breakpoints.down('sm')` — under 600px. */
const MUI_DOWN_SM_QUERY = "(max-width: 599.95px)";

/** MUI's spacing unit, which `paddingLeftDesktop` is counted in. */
const SPACING_PX = 8;

export interface ExpandableNotesLocation {
  id: number;
  name: string;
  notes: string | null;
}

export interface ExpandableNotesItem {
  id: number;
  notes: string | null;
  /** `finalized` hides every room's note control. */
  status: string;
  locations: readonly ExpandableNotesLocation[];
}

export interface ExpandableNotesRowProps {
  item: ExpandableNotesItem;
  /** A room's `Add a note` or its ⋮ `Edit`: the item and the room. */
  onLocationNote: (itemId: number, location: ExpandableNotesLocation) => void;
  /** The caller's row classes. */
  className?: string;
  /** MUI's `TableRow selected`. */
  selected?: boolean;
  /** The content cell's left padding on desktop, in MUI spacing units; 1 by default. */
  paddingLeftDesktop?: number;
  colSpan?: number;
  colSpanToggle?: number;
  /**
   * Each change counts as a new rooms array: the donor's caller denormalises
   * the rooms afresh on every write to its store, not only on theirs.
   */
  reopenKey?: unknown;
  /**
   * The old notes beside the new, where they differ from the base (#1104):
   * the item's own, and each room's by its location id — `null` (*None*)
   * where the base had none. Unset draws no change.
   */
  notesChanges?: NotesTableChanges;
}

/** A note, or its base text struck above it with the elbow; *None* on an empty side. */
function NotesChange({
  old,
  notes,
  className,
}: {
  old: OldValue | undefined;
  notes: string | null;
  className?: string;
}) {
  if (old === undefined) return <TruncatedNotes notes={notes} className={className} />;
  return (
    <OldBesideNew old={old} cutAt={OLD_NOTES_MAX} layout="block" className={className}>
      {notes ? <TruncatedNotes notes={notes} /> : null}
    </OldBesideNew>
  );
}

/** `showNotesCount`. */
function notesCountText(count: number) {
  return count === 1 ? `${count} Note` : `${count} Notes`;
}

/** `showLocationList`: ` • A, B, C` over more than one room, else nothing. */
function locationListText(locations: readonly ExpandableNotesLocation[]) {
  if (locations.length <= 1) return "";
  return locations
    .map((location, index) => (index === 0 ? ` • ${location.name || ""}` : location.name || ""))
    .join(", ");
}

export function ExpandableNotesRow({
  item,
  onLocationNote,
  className,
  selected = false,
  paddingLeftDesktop = 1,
  colSpan,
  colSpanToggle,
  reopenKey,
  notesChanges,
}: ExpandableNotesRowProps) {
  const isMobile = useMatchesMedia(MUI_DOWN_SM_QUERY);
  const [open, setOpen] = useState(false);

  const itemNote = item.notes;
  const isFinalized = item.status === "finalized";
  const locations = item.locations;
  const locationsCount = locations.length;
  const notesCount = (itemNote ? 1 : 0) + locations.filter((location) => location.notes).length;
  const showsToggle =
    (Boolean(itemNote) && locationsCount > 0) || (!itemNote && locationsCount > 1);

  const rendersLocation = locationsCount === 1 && !open;
  const rendersAllLocations = locationsCount > 1 && open;
  // A removed item note keeps its row, so the removal shows (#1104).
  const itemNoteOld = notesChanges?.item;
  const itemNoteShown = Boolean(itemNote) || itemNoteOld !== undefined;
  const rendersItemNote = itemNoteShown || rendersAllLocations;

  // `useEffect(() => { if (itemLocations?.length > 1) setNotesOpen(true) },
  // [itemLocations])`, as a render-time adjustment: each new rooms array —
  // or new `reopenKey` — opens the table over more than one room.
  const [openedFor, setOpenedFor] = useState<{
    locations: readonly ExpandableNotesLocation[];
    reopenKey: unknown;
  } | null>(null);
  if (openedFor?.locations !== locations || openedFor.reopenKey !== reopenKey) {
    setOpenedFor({ locations, reopenKey });
    if (locations.length > 1) setOpen(true);
  }

  const toggle = showsToggle ? (
    <IconButton
      aria-label="expand row"
      size="small"
      onClick={() => setOpen(!open)}
      className="absolute top-0"
    >
      {open ? <MuiKeyboardArrowUpIcon /> : <MuiKeyboardArrowDownIcon />}
    </IconButton>
  ) : null;

  const addNote = (location: ExpandableNotesLocation) => (
    <MuiLink asChild color="inherit">
      <button
        type="button"
        onClick={() => onLocationNote(item.id, location)}
        className={cn(
          MUI_TYPOGRAPHY_VARIANTS.body2,
          // MUI's `Link component='button'` reset (`Link.js:95-112`).
          "relative m-0 cursor-pointer rounded-none border-0 bg-transparent p-0 align-middle select-none",
          "focus-visible:outline-auto",
          "grow py-2 text-left",
        )}
      >
        Add a note
      </button>
    </MuiLink>
  );

  const renderLocation = (location: ExpandableNotesLocation, index?: number) => {
    const old = notesChanges?.rooms?.get(location.id);
    return (
      <div
        data-testid={`line-${item.id}-location-${location.id}`}
        className={cn(
          "flex flex-row justify-between text-center [&>:not(:first-child)]:ml-4",
          // The donor's `index < locations.length - 1`: the one room drawn
          // alone is called without an index, and draws no rule.
          index !== undefined && index < locationsCount - 1
            ? "border-spec-notes-rule border-b border-solid"
            : "border-b-0",
        )}
      >
        <MuiTypography
          variant="body2"
          component="p"
          className="flex min-w-[150px] items-center text-left font-medium"
        >
          {location.name}
        </MuiTypography>
        {location.notes ? (
          <>
            <NotesChange old={old} notes={location.notes} className="grow" />
            {!isFinalized ? (
              <div className="ml-0 flex flex-col items-end justify-start pr-[10.4px]">
                <EditMenu
                  onEdit={() => onLocationNote(item.id, location)}
                  trigger={
                    <IconMenuButtonTrigger
                      label="More options"
                      box="default"
                      color="primary"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <MoreVertGlyph />
                    </IconMenuButtonTrigger>
                  }
                />
              </div>
            ) : null}
          </>
        ) : old !== undefined ? (
          // A removed note: the old struck over *None* in the note's place, and
          // `Add a note` below it, so a note can be added back.
          <div className="flex grow flex-col items-start">
            <NotesChange old={old} notes={null} className="pt-2" />
            {!isFinalized ? addNote(location) : null}
          </div>
        ) : !isFinalized ? (
          addNote(location)
        ) : null}
      </div>
    );
  };

  const content = (
    <>
      <div className="m-0 p-0">
        {rendersItemNote ? (
          <div
            data-slot="notes-item-note"
            className={cn(
              "flex flex-row [&>:not(:first-child)]:ml-4",
              locationsCount > 0 && !open && "border-spec-notes-rule border-b border-solid",
            )}
          >
            <MuiTypography
              variant="body2"
              component="p"
              className="flex min-w-[150px] items-center font-medium"
            >
              {itemNoteShown ? "Note" : null}
            </MuiTypography>
            <NotesChange old={itemNoteOld} notes={itemNote} className="py-2" />
          </div>
        ) : null}
        {rendersLocation ? renderLocation(locations[0]!) : null}
        {open && rendersAllLocations
          ? locations.map((location, index) => (
              <div key={`location-${location.id}-${index}`}>{renderLocation(location, index)}</div>
            ))
          : null}
        {/* Folded away with the rooms' own notes, over more than one room. */}
        {notesChanges?.removedRooms && !(locationsCount > 1 && !open) ? (
          <RemovedRoomNotes rooms={notesChanges.removedRooms} />
        ) : null}
      </div>
      {!open ? (
        <MuiTypography
          data-slot="notes-summary"
          variant="body2"
          component="p"
          className={cn("pt-2 pb-1 pl-1 font-medium", locationsCount > 1 ? "flex" : "hidden")}
        >
          {locationsCount > 1 ? notesCountText(notesCount) : null}
          {locationsCount > 0 ? locationListText(locations) : null}
        </MuiTypography>
      ) : null}
    </>
  );

  if (isMobile) {
    return (
      <tr data-testid={`line-${item.id}-notes`} data-selected={selected || undefined}>
        <td colSpan={(colSpanToggle ?? 1) + (colSpan ?? 1)} className="border-0 p-0">
          <div
            className={cn(
              "flex flex-row items-center text-center [&>:not(:first-child)]:ml-2",
              className,
            )}
          >
            {toggle}
            {content}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <MuiBasicTableRow
      data-testid={`line-${item.id}-notes`}
      selected={selected}
      className={cn(
        // `ExpandedTableRow` (`ItemTableRows.tsx:7-64`): a white ground, the
        // cells' rules in `#F5F5F5`, no top rule and no vertical padding.
        !selected && "bg-white",
        "[&>td]:border-spec-notes-rule [&>td]:border-t-0 [&>td]:py-0",
        className,
      )}
    >
      <MuiBasicTableCell colSpan={colSpanToggle} className="relative pr-0 pl-2">
        {toggle}
      </MuiBasicTableCell>
      <MuiBasicTableCell
        scope="row"
        colSpan={colSpan}
        className="pr-0"
        style={{ paddingLeft: paddingLeftDesktop * SPACING_PX }}
      >
        {content}
      </MuiBasicTableCell>
    </MuiBasicTableRow>
  );
}
